import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, MessageSquare, FileText } from "lucide-react";
import { useBrochure } from "./BrochureProvider";

// Searchable knowledge index. type: "link" (scroll/route), "chat" (ask assistant), "brochure".
const INDEX = [
  { label: "About K.S.E. Inc.", keywords: "about who we are history 1998 production house", type: "link", target: "#about" },
  { label: "Our Factories (Mumbai, Umargam, Tiruppur)", keywords: "facilities factory factories location units mumbai gujarat tiruppur", type: "link", target: "#about" },
  { label: "Capabilities & Strength", keywords: "capabilities strength end to end under one roof merchandising", type: "link", target: "#capabilities" },
  { label: "Machinery & Assets", keywords: "machinery machines assets sewing cutting press capacity single needle overlock", type: "link", target: "#machinery" },
  { label: "Product Collection", keywords: "products collection garments t-shirts shirts nightwear kids casual knit woven", type: "link", target: "#collection" },
  { label: "360\u00B0 Virtual Showroom", keywords: "showroom 360 virtual tour display", type: "link", target: "/showroom", route: true },
  { label: "Global Clients & Brands", keywords: "clients brands customers countries asos lc waikiki superbalist international", type: "link", target: "#clients" },
  { label: "Certifications (ISO 14001, TUV Austria)", keywords: "certification iso 14001 tuv austria qro compliance quality", type: "link", target: "#certifications" },
  { label: "Contact & Enquiry", keywords: "contact enquiry address phone email map location", type: "link", target: "#contact" },
  { label: "Download Company Brochure", keywords: "brochure catalogue pdf download profile", type: "brochure" },
  { label: "What is your monthly capacity?", keywords: "capacity how many pieces production monthly", type: "chat" },
  { label: "What products do you manufacture?", keywords: "products manufacture make garments", type: "chat" },
  { label: "How do I place an order?", keywords: "order moq minimum quantity buy place", type: "chat" },
  { label: "Where are your factories located?", keywords: "where factory located location", type: "chat" },
];

export default function SearchBar({ variant = "desktop" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const brochure = useBrochure();

  const results = q.trim()
    ? INDEX.filter((i) => (i.label + " " + i.keywords).toLowerCase().includes(q.toLowerCase())).slice(0, 6)
    : INDEX.slice(0, 6);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const scrollTo = (target) => {
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(target)?.scrollIntoView({ behavior: "smooth" }), 250);
    } else {
      document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const choose = (item) => {
    setOpen(false);
    setQ("");
    if (item.type === "brochure") {
      brochure?.openBrochure();
    } else if (item.type === "chat") {
      window.dispatchEvent(new CustomEvent("kse-ask", { detail: item.label }));
    } else if (item.route) {
      navigate(item.target);
    } else {
      scrollTo(item.target);
    }
  };

  const iconFor = (t) => (t === "chat" ? MessageSquare : t === "brochure" ? FileText : ArrowRight);

  return (
    <div ref={boxRef} className={`relative ${variant === "desktop" ? "w-64" : "w-full"}`}>
      <div className="flex items-center gap-2 bg-white/15 focus-within:bg-white rounded-full px-4 py-2 transition-colors group">
        <Search size={17} className="text-white/70 group-focus-within:text-[#1a2a6b]" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search or ask a question..."
          className="bg-transparent outline-none text-sm w-full text-white placeholder:text-white/60 group-focus-within:text-slate-800 group-focus-within:placeholder:text-slate-400"
        />
      </div>

      {open && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50">
          <div className="px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 border-b border-slate-50">
            {q.trim() ? "Results" : "Popular"}
          </div>
          {results.length === 0 && (
            <button onClick={() => choose({ type: "chat", label: q })} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3">
              <MessageSquare size={16} className="text-[#2b3aa0]" />
              <span className="text-sm text-slate-700">Ask the assistant: "{q}"</span>
            </button>
          )}
          {results.map((item) => {
            const Icon = iconFor(item.type);
            return (
              <button
                key={item.label}
                onClick={() => choose(item)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-0"
              >
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.type === "chat" ? "bg-blue-50 text-[#2b3aa0]" : "bg-slate-100 text-[#1a2a6b]"}`}>
                  <Icon size={15} />
                </span>
                <span className="text-sm text-slate-700 flex-1">{item.label}</span>
                <span className="text-[10px] text-slate-400 uppercase">{item.type === "chat" ? "Ask" : item.type === "brochure" ? "PDF" : "Go"}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
